// ExternalAttrsExtension.js
//
// This extension displays asset information from the React application inside
// the Autodesk viewer. It creates a button in the viewer toolbar which toggles
// a draggable and resizable panel containing a React Tabulator table. The table
// reads its data from global variables populated by the React app.

import React from 'react';
import ReactDOM from 'react-dom/client';
import ViewerTable from '../components/ViewerTable.jsx';
import DraggableResizablePanel from '../components/DraggableResizablePanel.jsx';

const { Autodesk } = window;



class ExternalAttrsExtension extends Autodesk.Viewing.Extension {
    /**
     * Creates a new instance of the extension.
     * @param {Autodesk.Viewing.GuiViewer3D} viewer The viewer instance.
     * @param {object} [options] Optional extension options.
     */
    constructor(viewer, options) {
        super(viewer, options);
        this.viewer = viewer;

        // Toolbar button and control group created in createButtons()
        this.button = null;
        this.controlGroup = null;

        // Container DOM node and React root for the panel
        this.panel = null;
        this.reactRoot = null;

        /**
         * Listener for selection changes. Currently unused but kept for
         * future extension features.
         * @type {(event: Autodesk.Viewing.SelectionChangedEvent) => void}
         */
        this._onSelectionChanged = null;

        // Tracks if the panel is currently shown
        this._enabled = false;
    }

    /**
     * Called by the viewer when the extension is loaded.
     * Creates the toolbar UI.
     * @returns {boolean} True when loaded successfully.
     */
    load() {
        console.log("ExternalAttrsExtension loaded");
        this.createButtons();
        return true;
    }

    /**
     * Called by the viewer when the extension is unloaded.
     * Cleans up UI elements and React components.
     * @returns {boolean} True when unloaded successfully.
     */
    unload() {
        console.log("ExternalAttrsExtension unloaded");

        // Remove toolbar button and control group
        if (this.controlGroup) {
            this.viewer.toolbar.removeControl(this.controlGroup);
            this.controlGroup = null;
            this.button = null;
        }

        // Remove selection listener if it was registered
        if (this._onSelectionChanged) {
            this.viewer.removeEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, this._onSelectionChanged);
            this._onSelectionChanged = null;
        }

        // Unmount React and remove the panel
        if (this.panel) {
            if (this.reactRoot) {
                this.reactRoot.unmount();
                this.reactRoot = null;
            }
            this.panel.remove();
            this.panel = null;
        }
        return true;
    }

    /**
     * Creates the toolbar button used to toggle the panel.
     * The button is placed inside its own control group so it can be removed
     * easily when the extension is unloaded.
     */
    createButtons() {
        // Create a new button.
        this.button = new Autodesk.Viewing.UI.Button("externalAttrsButton");
        this.button.addClass('bi');
        this.button.addClass('bi-info-circle');
        this.button.setToolTip("Show ACC assets");
        // Set initial state to INACTIVE.
        this.button.setState(Autodesk.Viewing.UI.Button.State.INACTIVE);

        // Toggle state on click.
        this.button.onClick = () => {
            if (this._enabled) {
                this.button.setState(Autodesk.Viewing.UI.Button.State.INACTIVE);
                console.log("Show ACC assets deactivated");
                this.disableExternalAttrsLogging();
            } else {
                this.button.setState(Autodesk.Viewing.UI.Button.State.ACTIVE);
                console.log("Show ACC assets activated");
                this.enableExternalAttrsLogging();
            }
        };

        // Create a control group and add the button to it.
        this.controlGroup = new Autodesk.Viewing.UI.ControlGroup("externalAttrsControlGroup");
        this.controlGroup.addControl(this.button);
        // Add the control group to the viewer toolbar.
        this.viewer.toolbar.addControl(this.controlGroup);
    }

    /**
     * Creates and shows the React panel containing the asset table.
     * The panel is rendered using ReactDOM into a DOM node appended to the
     * viewer container.
     */
    enableExternalAttrsLogging() {
        this._enabled = true;

        console.log("Asset Attributes extension activated");

        // Remove any existing panel before creating a new one
        if (this.panel) {
            this.disableExternalAttrsLogging();
        }

        console.log("Creating draggable/resizable React component panel...");

        // Create a container div for the draggable panel
        this.panel = document.createElement('div');
        this.panel.id = 'external-attrs-panel';
        this.panel.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 1000;
            pointer-events: none;
        `;

        // Allow pointer events on the panel itself
        this.panel.addEventListener('mousedown', (e) => {
            e.stopPropagation();
        });

        // Attach the panel to the viewer container
        this.viewer.container.appendChild(this.panel);

        // Access global helper functions and data provided by the React app
        const { selectedProjectId, selectedCategoryId, assetFetchOptions, reloadFlag, setAssetFetchOptions, setReloadFlag } = window.assetDataProps || {};
        const getAccessToken = window.getAuthAccessToken; // Get getAccessToken from global scope
        
        // Ensure the authentication helper exists before continuing
        if (!getAccessToken) {
            console.error('getAccessToken is not available on window.getAuthAccessToken');
            console.log('Available window properties:', Object.keys(window).filter(key => key.includes('auth') || key.includes('Access')));
            return;
        }

        // Create React root and render the draggable panel with the table
        // The table component reads its data from window.assetDataProps.currentTableData
        this.reactRoot = ReactDOM.createRoot(this.panel);
        this.reactRoot.render(
            React.createElement(DraggableResizablePanel, {
                onClose: () => this.disableExternalAttrsLogging()
            },
                React.createElement(ViewerTable)
            )
        );

        console.log("Draggable/resizable React component panel created and added to DOM");
    }

    /**
     * Removes the React panel and cleans up resources.
     */
    disableExternalAttrsLogging() {
        this._enabled = false;
        console.log("External Attributes extension deactivated");
        if (this.panel) {
            if (this.reactRoot) {
                this.reactRoot.unmount();
                this.reactRoot = null;
            }
            this.panel.remove();
            this.panel = null;
        }
    }
}

// Register the extension with the viewer and expose it globally so it can be
// loaded dynamically from the React application.
Autodesk.Viewing.theExtensionManager.registerExtension("ExternalAttrsExtension", ExternalAttrsExtension);
window.ExternalAttrsExtension = ExternalAttrsExtension;